import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Table, Button, Tag, message, Space, Descriptions, Modal, Card, Row, Col, Statistic } from 'antd';
import { CheckOutlined, CloseOutlined, EyeOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, FilePdfOutlined, UserAddOutlined, UserDeleteOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { ensureUploadMode, getUploadMode } from '../utils/uploadMode';
import { previewOssPdf } from '../utils/ossUpload';
import AppHeader from '../components/AppHeader';
import { useTheme } from '../context/ThemeContext';

const { Content } = Layout;

const actionMap = {
  update: { text: '修改图纸', color: 'cyan' },
  delete: { text: '删除图纸', color: 'red' },
  download_3d: { text: '下载3D图', color: 'purple' }
};

const statusMap = {
  pending: { text: '待审批', color: 'orange' },
  approved: { text: '已通过', color: 'green' },
  rejected: { text: '已拒绝', color: 'red' },
  execute_failed: { text: '执行失败', color: 'red' }
};


// 字段名中文映射表
const FIELD_NAME_MAP = {
  // 基础信息
  creator: '制图人',
  draw_date: '制图日期',
  frame_type: '机架类型',
  probe_type: '探头类型',

  // 探头开口尺寸
  probe_open_width: '探头开口宽度',
  probe_open_height: '探头开口高度',
  probe_size: '探头开口尺寸',

  // 可通过尺寸
  passable_width: '可通过宽度',
  passable_height: '可通过高度',
  passable_size: '可通过尺寸',

  // 探头外形尺寸
  probe_length: '探头长度',
  probe_width: '探头宽度',
  probe_height: '探头高度',
  probe_outer_size: '探头外形尺寸',

  // 整机外形尺寸
  machine_length: '整机长度',
  machine_width: '整机宽度',
  machine_height: '整机高度',
  machine_outer_size: '整机外形尺寸',

  // 机架
  frame_length: '机架长度',

  // 皮带信息
  belt_material: '皮带材质',
  belt_thickness: '皮带厚度',
  belt_size: '皮带尺寸',

  // 皮带输送面离地高度
  belt_height_value: '皮带高度数值',
  belt_height_symbol: '皮带高度符号',
  belt_height_tolerance: '皮带高度公差',
  belt_height: '皮带输送面离地高度',

  // 其他
  belt_direction: '皮带运行方向',
  control_box_design: '控制箱与机身设计',
  alarm_reject_mode: '报警剔除方式',
  other_features: '其他特点',

  // 文件相关
  scheme_pdf: '方案图PDF',
  scheme_dwg: '方案图DWG',
  sheet_dwg: '钣金图DWG',
  machining_dwg: '机加工DWG',
  frame_dwg: '架子图DWG',
  probe_dwg: '探头图DWG',
  three_d_stp: '3D图',
};

// 获取字段中文名
const getFieldLabel = (fieldName) => FIELD_NAME_MAP[fieldName] || fieldName;

const filterButtons = [
  { key: 'all', label: '全部' },
  { key: 'pending', label: '待审批' },
  { key: 'approved', label: '已通过' },
  { key: 'rejected', label: '已拒绝' }
];

function parseActionDetail(detail) {
  if (!detail) return {};
  if (typeof detail === 'object') return detail;
  try {
    return JSON.parse(detail);
  } catch (e) {
    return {};
  }
}

function formatTime(text) {
  if (!text) return '-';
  try {
    return new Date(text).toLocaleString('zh-CN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    }).replace(/\//g, '-');
  } catch (e) {
    return '-';
  }
}

const DrawingApprovalsPage = () => {
  const [data, setData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [delegateModalVisible, setDelegateModalVisible] = useState(false);
  const [delegations, setDelegations] = useState(() => {
    // 从localStorage读取缓存的授权状态，避免页面刷新后状态丢失
    const cached = localStorage.getItem('approval_delegations');
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (e) {
        return [];
      }
    }
    return [];
  });
  const [delegateLoading, setDelegateLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { isDarkMode, getBackgroundColor, getTextColor, getTextColorSecondary } = useTheme();

  const isLizhiqiang = user.name === '李志强';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/drawing-approvals');
      const requests = res.data || [];
      setAllData(requests);

      const pendingCount = requests.filter(r => r.status === 'pending').length;
      const approvedCount = requests.filter(r => r.status === 'approved').length;
      const rejectedCount = requests.filter(r => r.status === 'rejected').length;
      setStats({ pending: pendingCount, approved: approvedCount, rejected: rejectedCount });

      applyFilter(requests, activeFilter);
    } catch (err) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  // 页面加载时获取授权列表（确保刷新页面后状态正确）
  useEffect(() => {
    if (isLizhiqiang) {
      fetchDelegations();
    }
  }, [isLizhiqiang]);

  // Modal 打开时强制重新获取授权列表（确保最新状态）
  useEffect(() => {
    if (delegateModalVisible && isLizhiqiang) {
      fetchDelegations();
    }
  }, [delegateModalVisible, isLizhiqiang]);

  const fetchDelegations = async () => {
    try {
      const res = await api.get('/drawing-approvals/delegations');
      const delegationData = res.data || [];
      setDelegations(delegationData);
      // 缓存到 localStorage，用于页面刷新后快速恢复显示
      localStorage.setItem('approval_delegations', JSON.stringify(delegationData));
    } catch (err) {
      console.error('获取授权列表失败', err);
      // 如果接口失败，尝试从缓存恢复
      const cached = localStorage.getItem('approval_delegations');
      if (cached) {
        try {
          setDelegations(JSON.parse(cached));
        } catch (e) {
          setDelegations([]);
        }
      } else {
        setDelegations([]);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const applyFilter = (requests, filter) => {
    let filtered = requests;
    switch (filter) {
      case 'pending':
        filtered = requests.filter(r => r.status === 'pending');
        break;
      case 'approved':
        filtered = requests.filter(r => r.status === 'approved');
        break;
      case 'rejected':
        filtered = requests.filter(r => r.status === 'rejected');
        break;
      default:
        filtered = requests;
    }
    setData(filtered);
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    applyFilter(allData, filter);
  };

  const handleApprove = async (id) => {
    try {
      const res = await api.put(`/drawing-approvals/${id}/approve`);
      message.success(res.data.message || '审批通过');
      window.dispatchEvent(new CustomEvent('refresh-pending-count'));
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.message || '审批失败');
    }
  };

  const handleReject = async (id) => {
    try {
      await api.put(`/drawing-approvals/${id}/reject`);
      message.success('已拒绝');
      window.dispatchEvent(new CustomEvent('refresh-pending-count'));
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.message || '操作失败');
    }
  };

  const showDetail = async (record) => {
    setDetailLoading(true);
    try {
      const res = await api.get(`/drawing-approvals/${record.id}`);
      setDetail(res.data);
      setDetailVisible(true);
    } catch (err) {
      message.error('获取详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleGrantAll = async () => {
    setDelegateLoading(true);
    try {
      const res = await api.post('/drawing-approvals/delegate', {
        action: 'grant'
      });
      message.success(res.data.message);
      // 直接使用后端返回的 delegations 数组更新状态
      const newDelegations = res.data.delegations || [];
      setDelegations(newDelegations);
      // 同步更新缓存
      localStorage.setItem('approval_delegations', JSON.stringify(newDelegations));
    } catch (err) {
      message.error(err.response?.data?.message || '授权操作失败');
    } finally {
      setDelegateLoading(false);
    }
  };

  const handleRevokeAll = async () => {
    setDelegateLoading(true);
    try {
      const res = await api.post('/drawing-approvals/delegate', {
        action: 'revoke'
      });
      message.success(res.data.message);
      // 直接使用后端返回的 delegations 数组更新状态（撤销后为空数组）
      const newDelegations = res.data.delegations || [];
      setDelegations(newDelegations);
      // 同步更新缓存
      localStorage.setItem('approval_delegations', JSON.stringify(newDelegations));
    } catch (err) {
      message.error(err.response?.data?.message || '撤销授权失败');
    } finally {
      setDelegateLoading(false);
    }
  };

  // 预览审批详情中的文件（仅支持PDF）
  const handlePreviewFile = async (filePath, fileName, drawingId, deviceType) => {
    if (!filePath) {
      message.error('文件路径不存在');
      return;
    }
    const ext = (fileName || filePath).slice((fileName || filePath).lastIndexOf('.')).toLowerCase();
    if (ext !== '.pdf') {
      message.info('仅PDF文件支持预览');
      return;
    }
    try {
      const pathParts = filePath.split('/');
      const fileType = pathParts[pathParts.length - 2];

      if (!fileType || !drawingId || !deviceType) {
        message.error('无法确定文件类型或图纸信息');
        return;
      }

      await ensureUploadMode();
      if (getUploadMode() === 'oss') {
        await previewOssPdf(`/drawings/${deviceType}/${drawingId}/preview/${fileType}`);
      } else {
        const response = await api.get(`/drawings/${deviceType}/${drawingId}/preview/${fileType}`, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 30000);
      }
    } catch (err) {
      message.error('预览失败');
    }
  };

  const renderDrawingInfo = (d) => {
    if (!d) return null;
    return (
      <>
        <Descriptions.Item label="图纸编号">{d.drawingNo || d.drawing_no || d.previewDrawingNo || '-'}</Descriptions.Item>
        <Descriptions.Item label="制图人">{d.drawingInfo?.creator || d.formData?.creator || '-'}</Descriptions.Item>
        <Descriptions.Item label="制图日期">{d.drawingInfo?.drawDate || d.formData?.draw_date || '-'}</Descriptions.Item>
        <Descriptions.Item label="机架类型">{d.drawingInfo?.frameType || d.formData?.frame_type || '-'}</Descriptions.Item>
        <Descriptions.Item label="探头类型">{d.drawingInfo?.probeType || d.formData?.probe_type || '-'}</Descriptions.Item>
        <Descriptions.Item label="探头开口尺寸">{d.drawingInfo?.probeSize || d.computedFields?.probe_size || d.formData?.probe_size || '-'}</Descriptions.Item>
        <Descriptions.Item label="可通过尺寸">{d.drawingInfo?.passableSize || d.computedFields?.passable_size || d.formData?.passable_size || '-'}</Descriptions.Item>
        <Descriptions.Item label="探头外形尺寸">{d.drawingInfo?.probeOuterSize || d.computedFields?.probe_outer_size || d.formData?.probe_outer_size || '-'}</Descriptions.Item>
        <Descriptions.Item label="机架长度">{d.drawingInfo?.frameLength || d.formData?.frame_length || '-'}</Descriptions.Item>
        <Descriptions.Item label="整机外形尺寸">{d.drawingInfo?.machineOuterSize || d.computedFields?.machine_outer_size || d.formData?.machine_outer_size || '-'}</Descriptions.Item>
        <Descriptions.Item label="皮带厚度">{d.drawingInfo?.beltThickness || d.formData?.belt_thickness || '-'}</Descriptions.Item>
        <Descriptions.Item label="皮带材质">{d.drawingInfo?.beltMaterial || d.formData?.belt_material || '-'}</Descriptions.Item>
        <Descriptions.Item label="皮带尺寸">{d.drawingInfo?.beltSize || d.formData?.belt_size || '-'}</Descriptions.Item>
        <Descriptions.Item label="皮带输送面离地高度">{d.drawingInfo?.beltHeight || d.computedFields?.belt_height || d.formData?.belt_height || '-'}</Descriptions.Item>
        <Descriptions.Item label="皮带运行方向">{d.drawingInfo?.beltDirection || d.formData?.belt_direction || '-'}</Descriptions.Item>
        <Descriptions.Item label="控制箱设计">{d.drawingInfo?.controlBoxDesign || d.formData?.control_box_design || '-'}</Descriptions.Item>
        <Descriptions.Item label="报警剔除方式">{d.drawingInfo?.alarmRejectMode || d.formData?.alarm_reject_mode || '-'}</Descriptions.Item>
        <Descriptions.Item label="其他特点" span={2}>{d.drawingInfo?.otherFeatures || d.formData?.other_features || '-'}</Descriptions.Item>
      </>
    );
  };

  const renderFilePreview = (fileInfo, drawingId, deviceType) => {
    if (!fileInfo || Object.keys(fileInfo).length === 0) return '无';

    return (
      <div>
        {Object.keys(fileInfo).map(k => {
          const file = fileInfo[k];
          const isPdf = (file.name || '').toLowerCase().endsWith('.pdf');
          return (
            <div key={k} style={{ marginBottom: 8, padding: '6px 10px', background: isDarkMode ? '#1f1f1f' : '#f5f5f5', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Space>
                <Tag color="blue">{k}</Tag>
                <span style={{ color: getTextColor() }}>{file.name}</span>
              </Space>
              {isPdf && (
                <Button 
                  size="small" 
                  icon={<FilePdfOutlined />}
                  onClick={() => handlePreviewFile(file.path, file.name, drawingId, deviceType)}
                >
                  预览
                </Button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderDetailContent = () => {
    if (!detail) return null;
    const d = parseActionDetail(detail.action_detail);
    const drawingId = d?.drawingId;
    const deviceType = d?.deviceType;

    const commonItems = [
      <Descriptions.Item label="操作类型" key="action">{actionMap[detail.action]?.text || detail.action}</Descriptions.Item>,
      <Descriptions.Item label="设备类型" key="deviceType">{d.deviceTypeCN || d.deviceType || '-'}</Descriptions.Item>,
      <Descriptions.Item label="申请人" key="requester">{detail.requester_name || '-'}</Descriptions.Item>,
      <Descriptions.Item label="申请时间" key="time">{formatTime(detail.created_at)}</Descriptions.Item>,
    ];

    if (detail.action === 'update') {
      const hasFileChanges = d.fileInfo && typeof d.fileInfo === 'object' && !Array.isArray(d.fileInfo) && Object.keys(d.fileInfo).length > 0;

      return (
        <Descriptions bordered column={2} size="small">
          {commonItems}
          {renderDrawingInfo(d)}

          <Descriptions.Item label="变更字段" span={2}>
            {d.changedFieldDetails && d.changedFieldDetails.length > 0 ? (
              <div style={{ lineHeight: '2', padding: '8px 12px', background: isDarkMode ? '#2b2111' : '#fff7e6', borderRadius: 4, border: isDarkMode ? '1px solid #594214' : '1px solid #ffd591' }}>
                {d.changedFieldDetails.map((item, index) => (
                  <span key={index}>
                    <Tag color="orange">{getFieldLabel(item.field)}</Tag>
                    <span style={{ color: '#999', textDecoration: 'line-through' }}>
                      {String(item.oldValue)}
                    </span>
                    <span style={{ color: '#666', margin: '0 4px' }}>→</span>
                    <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                      {String(item.newValue)}
                    </span>
                    {index < d.changedFieldDetails.length - 1 && (
                      <span style={{ color: '#999', margin: '0 8px' }}>；</span>
                    )}
                  </span>
                ))}
              </div>
            ) : d.changedFields?.length > 0 ? (
              d.changedFields.map(f => getFieldLabel(f)).join('、')
            ) : (
              '无'
            )}
          </Descriptions.Item>

          <Descriptions.Item label="文件变更" span={2}>
            {hasFileChanges ? (
              <div>
                <div style={{ marginBottom: 8 }}>
                  <Tag color="green">新上传</Tag>
                </div>
                {renderFilePreview(d.fileInfo, drawingId, deviceType)}
              </div>
            ) : (
              <span style={{ color: '#999' }}>无文件变更</span>
            )}
          </Descriptions.Item>

          {d.replacedFiles && Array.isArray(d.replacedFiles) && d.replacedFiles.length > 0 && (
            <Descriptions.Item label="被替换文件" span={2}>
              <div style={{ color: '#ff4d4f' }}>
                {d.replacedFiles.map((f, i) => (
                  <div key={i} style={{ textDecoration: 'line-through', marginBottom: 2 }}>
                    {f}
                  </div>
                ))}
              </div>
            </Descriptions.Item>
          )}
        </Descriptions>
      );
    }

    if (detail.action === 'delete') {
      return (
        <Descriptions bordered column={2} size="small">
          {commonItems}
          {renderDrawingInfo(d)}
          <Descriptions.Item label="删除原因" span={2}>{d.reason || '-'}</Descriptions.Item>
        </Descriptions>
      );
    }

    if (detail.action === 'download_3d') {
      return (
        <Descriptions bordered column={2} size="small">
          {commonItems}
          {renderDrawingInfo(d)}
          <Descriptions.Item label="文件信息" span={2}>
            {d.fileName ? (
              <div>
                <Tag color="purple">3D图</Tag>
                <span>{d.fileName}</span>
              </div>
            ) : '无'}
          </Descriptions.Item>
        </Descriptions>
      );
    }

    return <pre>{JSON.stringify(d, null, 2)}</pre>;
  };

  const columns = [
    { title: '审批编号', dataIndex: 'id', width: 80, align: 'center' },
    { 
      title: '图纸编号', 
      dataIndex: 'action_detail', 
      width: 150, 
      align: 'center',
      render: (detail) => {
        const d = parseActionDetail(detail);
        return d.drawingNo || d.previewDrawingNo || d.drawing_no || d.formData?.drawing_no || '-';
      }
    },
    { 
      title: '操作类型', 
      dataIndex: 'action', 
      width: 110, 
      align: 'center',
      render: (action) => <Tag color={actionMap[action]?.color}>{actionMap[action]?.text || action}</Tag> 
    },
    { title: '申请人', dataIndex: 'requester_name', width: 100, align: 'center', render: (text) => text || '-' },
    { title: '审批人', dataIndex: 'approver_name', width: 100, align: 'center', render: (text) => text || '-' },
    { 
      title: '状态', 
      dataIndex: 'status', 
      width: 100, 
      align: 'center',
      render: (status) => <Tag color={statusMap[status]?.color}>{statusMap[status]?.text || status}</Tag> 
    },
    { 
      title: '申请时间', 
      dataIndex: 'created_at', 
      width: 170, 
      align: 'center',
      render: (text) => formatTime(text) 
    },
    { 
      title: '审批时间', 
      key: 'approval_time', 
      width: 170, 
      align: 'center',
      render: (_, record) => formatTime(record.approval_time)
    },
    {
      title: '操作',
      width: 220,
      fixed: 'right',
      align: 'center',
      render: (_, record) => {
        if (['approved', 'rejected', 'execute_failed'].includes(record.status)) {
          if (record.status === 'rejected') {
            const rejecterName = record.approver_name;
            if (rejecterName) {
              return <Tag color="red">{rejecterName}拒绝</Tag>;
            }
            return <Tag color={statusMap[record.status]?.color}>{statusMap[record.status]?.text}</Tag>;
          }
          return <Tag color={statusMap[record.status]?.color}>{statusMap[record.status]?.text}</Tag>;
        }
        if (record.requested_by === user.id) {
          return <Tag>自己的申请</Tag>;
        }
        return (
          <Space size={4}>
            <Button type="primary" size="small" icon={<CheckOutlined />} onClick={() => handleApprove(record.id)}>通过</Button>
            <Button danger size="small" icon={<CloseOutlined />} onClick={() => handleReject(record.id)}>拒绝</Button>
            <Button size="small" icon={<EyeOutlined />} onClick={() => showDetail(record)}>详情</Button>
          </Space>
        );
      }
    }
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: getBackgroundColor() }}>
      <AppHeader />
      <Content style={{ padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div>
                        <h2 style={{ margin: 0, marginBottom: 8, fontSize: 24, fontWeight: 600, color: getTextColor() }}>图纸审批</h2>
            <p style={{ color: getTextColorSecondary(), margin: 0 }}>共 {allData.length} 条审批记录</p>
          </div>
          {isLizhiqiang && (
            <Button 
              type="primary" 
              icon={<UserAddOutlined />}
              onClick={() => setDelegateModalVisible(true)}
            >
              授权管理
            </Button>
          )}
        </div>

        <Row gutter={24} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Card style={{ 
              background: isDarkMode ? '#1f1f1f' : '#1f2937', 
              border: isDarkMode ? '1px solid #434343' : '1px solid #374151'
            }}>
              <Statistic 
                title={<span style={{ color: '#9ca3af' }}>待审批</span>}
                value={stats.pending}
                prefix={<ClockCircleOutlined style={{ color: '#fbbf24', fontSize: 24, marginRight: 8 }} />}
                valueStyle={{ color: '#fff', fontSize: 32, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card style={{ 
              background: isDarkMode ? '#1f1f1f' : '#1f2937', 
              border: isDarkMode ? '1px solid #434343' : '1px solid #374151'
            }}>
              <Statistic 
                title={<span style={{ color: '#9ca3af' }}>已通过</span>}
                value={stats.approved}
                prefix={<CheckCircleOutlined style={{ color: '#34d399', fontSize: 24, marginRight: 8 }} />}
                valueStyle={{ color: '#fff', fontSize: 32, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card style={{ 
              background: isDarkMode ? '#1f1f1f' : '#1f2937', 
              border: isDarkMode ? '1px solid #434343' : '1px solid #374151'
            }}>
              <Statistic 
                title={<span style={{ color: '#9ca3af' }}>已拒绝</span>}
                value={stats.rejected}
                prefix={<CloseCircleOutlined style={{ color: '#f87171', fontSize: 24, marginRight: 8 }} />}
                valueStyle={{ color: '#fff', fontSize: 32, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        </Row>

        <Space style={{ marginBottom: 24 }} size={12}>
          {filterButtons.map(btn => (
            <Button
              key={btn.key}
              type={activeFilter === btn.key ? 'primary' : 'default'}
              onClick={() => handleFilterChange(btn.key)}
              style={{
                borderRadius: 8,
                padding: '6px 20px',
                background: activeFilter === btn.key ? '#3b82f6' : (isDarkMode ? '#374151' : '#374151'),
                borderColor: activeFilter === btn.key ? '#3b82f6' : '#4b5563',
                color: '#fff'
              }}
            >
              {btn.label}
            </Button>
          ))}
        </Space>

        <Table 
          columns={columns} 
          dataSource={data} 
          rowKey="id" 
          loading={loading} 
          bordered 
          scroll={{ x: 'max-content' }}
          size="small"
        />

        <Modal 
          title={<span style={{ color: getTextColor() }}>审批详情</span>} 
          open={detailVisible} 
          onCancel={() => setDetailVisible(false)} 
          footer={null} 
          width={900}
          style={{ top: 20 }}
        >
          {detailLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: getTextColor() }}>加载中...</div>
          ) : (
            renderDetailContent()
          )}
        </Modal>

        {/* 授权管理 Modal - destroyOnClose=false 避免 Modal 关闭后内容被销毁 */}
        <Modal
          title="审批授权管理"
          open={delegateModalVisible}
          onCancel={() => { setDelegateModalVisible(false); }}
          footer={null}
          width={500}
          destroyOnClose={false}
        >
          <div style={{ marginBottom: 24 }}>
            <h4 style={{ color: getTextColor(), marginBottom: 12 }}>当前授权状态：</h4>
            {delegations.length === 0 ? (
              <div style={{ 
                padding: '16px', 
                background: isDarkMode ? '#2b1d1d' : '#fff2f0', 
                borderRadius: 8,
                border: isDarkMode ? '1px solid #581414' : '1px solid #ffccc7'
              }}>
                <p style={{ color: '#ff4d4f', margin: 0 }}>⚠️ 暂未授权</p>
                <p style={{ color: getTextColorSecondary(), margin: '8px 0 0 0', fontSize: 13 }}>
                  未授权时：其他设计师的申请由李志强审核，李志强的申请由张振南审核
                </p>
              </div>
            ) : (
              <div style={{ 
                padding: '16px', 
                background: isDarkMode ? '#162312' : '#f6ffed', 
                borderRadius: 8,
                border: isDarkMode ? '1px solid #274916' : '1px solid #b7eb8f'
              }}>
                <p style={{ color: '#52c41a', margin: 0, fontWeight: 500 }}>✓ 已授权人员：</p>
                <Space wrap style={{ marginTop: 8 }}>
                  {delegations.map(d => (
                    <Tag 
                      key={d.id} 
                      color="blue" 
                      style={{ fontSize: 14, padding: '4px 8px' }}
                    >
                      {d.delegatee_name}
                    </Tag>
                  ))}
                </Space>
                <p style={{ color: getTextColorSecondary(), margin: '12px 0 0 0', fontSize: 13 }}>
                  授权后：张振南和李永俊可互相审核，也可审核其他设计师的申请
                </p>
              </div>
            )}
          </div>
          
          <div style={{ 
            padding: '16px', 
            background: isDarkMode ? '#1f1f1f' : '#f5f5f5', 
            borderRadius: 8,
            marginBottom: 16
          }}>
            <h4 style={{ color: getTextColor(), marginBottom: 12 }}>授权操作：</h4>
            <Space>
              <Button 
                type="primary" 
                icon={<UserAddOutlined />}
                onClick={handleGrantAll}
                loading={delegateLoading}
                disabled={delegations.length >= 2}
              >
                一键授权（张振南+李永俊）
              </Button>
              <Button 
                danger 
                icon={<UserDeleteOutlined />}
                onClick={handleRevokeAll}
                loading={delegateLoading}
                disabled={delegations.length === 0}
              >
                一键撤销授权
              </Button>
            </Space>
            <p style={{ color: getTextColorSecondary(), margin: '12px 0 0 0', fontSize: 12 }}>
              注意：授权操作会同时影响张振南和李永俊两人的审批权限
            </p>
          </div>
        </Modal>
      </Content>
    </Layout>
  );
};

export default DrawingApprovalsPage;
